import {
  extractSchema,
  getArrayItemSchema,
  isSchemaAssignable,
  getConstantSchema,
  getUnionSchema,
  getObjectPropertySchema,
  getSchemaAnnotation,
  getRequiredObjectSchemaProperties,
  isSubsetSchema,
} from '@sapappgyver/appgyver-schemas';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import { parseExpression } from '@sapappgyver/appgyver-formulas/parser';
import upperFirst from 'lodash/upperFirst';
import {
  ComponentPropertyBinding,
  ComponentPropertyBindingType,
  NodeRedBinding,
  StyleBinding,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { Schema } from '@sapappgyver/appgyver-descriptors/lib/common/schemas';
import omit from 'lodash/omit';
import { convertBindingToExpression, importCachedExpression, getExpressionContextSchema } from './expressions';
import { getDefaultValueForSchema, isBindingSchemaCompatible } from './typings';
import {
  STRING_WILDCARD_SCHEMA,
  NUMBER_SCHEMA,
  BOOLEAN_SCHEMA,
  ARRAY_SCHEMA,
  OBJECT_SCHEMA,
  ANY_SCHEMA,
  STRING_UNFORMATTED_SCHEMA,
  CLOUD_FUNCTION_REFERENCE_SCHEMA,
  SPA_PROCESS_DEFINITION_REFERENCE_SCHEMA,
  BUILD_ACTION_REFERENCE_SCHEMA,
  BTP_DESTINATION_REFERENCE_SCHEMA,
  HTTP_REQUEST_METHOD_REFERENCE_SCHEMA,
  HTTP_REQUEST_BODY_REFERENCE_SCHEMA,
  UNKNOWN_SCHEMA,
} from './schemas';
import { defaultRepeatName } from './repeats';
import isReferenceBindableToDirection from '../composer/references/isReferenceBindableToDirection';
import { Binding } from './binding-types';
import { ReferenceOption } from '../composer/references/types';

export function getBindingSource(
  binding: StyleBinding | NodeRedBinding | ComponentPropertyBinding | Binding<unknown> | undefined,
): string | undefined {
  if (binding == null) {
    return undefined;
  }
  if (typeof binding === 'object') {
    if ('source' in binding) {
      const { source } = binding;
      if (Array.isArray(source)) {
        return 'array';
      }
      if (typeof source === 'object' && source != null) {
        return 'object';
      }
      return source;
    }
    return undefined;
  }
  if (typeof binding === 'string') {
    return 'str';
  }
  if (typeof binding === 'number') {
    return 'num';
  }
  if (typeof binding === 'boolean') {
    return 'bool';
  }
  return undefined;
}

export function getBindingKey(
  binding: StyleBinding | NodeRedBinding | ComponentPropertyBinding | Binding<unknown> | undefined,
): unknown {
  if (binding == null) {
    return undefined;
  }
  if (typeof binding === 'object') {
    if ('key' in binding) {
      return binding.key;
    }
    if ('value' in binding) {
      return binding.value;
    }
  }
  return binding;
}

/**
 * In some cases bindings cannot be null, or the runtime could break.
 * In those cases you can use a formula binding that evaluates to a null value.
 */
export const DUMMY_BINDING = {
  source: 'expression',
  key: parseExpression('null').export(),
};

export const PREVIEW_VALUE_TOOLTIP_TEXT =
  'The optional preview value is used by the view canvas only, so your components look as expected without live data when editing your app. It has no effect on the app runtime. Leave it blank to use smart example preview values on the canvas.';

export const BINDING_TYPE_REGULAR_DESCRIPTION = `**Configuration and two-way properties**

Configuration properties are used to bring in data and values for the component to use, such as the keyboard type of an input, or options list for a dropdown.

A component cannot modify its regular configuration properties from the "inside". An exception is a property set to use two-way binding. In this case, both changes in bound data and user interaction with the component can change the property, such as an input field's **Value**.

Inside component template editor, properties defined here are available via the **Composite component internal property** binding type. Regular configuration properties are read-only in component template editor, while two-way bound properties can be set with the **Set internal property** flow function.`;

export const BINDING_TYPE_INBOUND_DESCRIPTION = `**Configuration property**

Component configuration properties are a way to bring data into component. They cannot be modified by the component's internal logic.

For example, a button with a built-in spinner could have a "Show spinner" regular property and a text paragraph has the "Content" property.

You can use all binding types with regular properties. If the binding is dynamic and changes (e.g. when you use a page variable), the component will react to the new value accordingly. Configuration properties cannot be changed in component template editor.`;

export const BINDING_TYPE_OUTBOUND_DESCRIPTION = `**Output values**

Component output values are a way for the component to "broadcast" data to the rest of the app.

For example, a file upload component could have its "Upload progress" available as an output value.

You cannot bind anything to output values, but they are available via the Binding Editor, so you can use them in formulas or as direct bindings. In component template editor, output values can be set with the **Set output value** flow function.`;

export const BINDING_TYPE_TWO_WAY_DESCRIPTION = `**Two-way properties**

Two-way component properties can be modified both by the component's internal logic, as well as by setting it to a new value from the outside.

The prime example is an input field's "Value". If you bind it to a page variable, changes to the page variable change what's displayed in the input. When the user types something into the field, the new value is propagated to the bound variable.

You can only bind dynamic variables to two-way properties, i.e. app, page and data variables. In component template editor, two-way properties can be set with the **Set internal property** flow function.`;

export const BINDING_TYPE_PRIVATE_DESCRIPTION = `**Private variables**

Private variables are only usable in component template editor. They can be used to write and read data throughout the component's internal lifecycle, without making that data available from the "outside" when using the component in an app.

Private variables can be set with the **Set private variable** flow function in component template editor.`;

export const BINDING_TYPE_DATA_INTERFACE_DESCRIPTION = `**Data adapter**

Data adapters are a way to connect a component directly to a data entity in the app. The component can perform operations on the connected resource via its internal data flow functions.

The adapter defines some requirements for the connected resource.

For example, a data adapter that is used in the component for listing and creating records will require the corresponding operations from the connected resource.

The data adapter schema can be fixed, requiring a certain number of fields with fixed types, or arbitrary, specifying only accepted field types and a minimum/maximum number of fields.
`;

const REPEAT_NAMESPACE = 'repeated_';

const displayTitlesBySource: Record<string, string> = {
  'str': 'static text',
  'num': 'static number',
  'bool': 'static true/false',
  'array': 'list of values',
  'object': 'object with properties',
  'app-variable': 'app variable',
  'page-variable': 'page variable',
  'parameter': 'page parameter',
  'data': 'data variable',
  'repeated-legacy': 'property of data item in repeat (deprecated)',
  'repeated': 'data item in repeat',
  'translation': 'translation variable',
  'theme': 'theme variable',
  'sensor-variable': 'sensor variable',
  'system-variable': 'system variable',
  'expression': 'formula',
  'component': "another component's property or output value",
  'self': "selected component's property or output value",
  'internal-property': 'composite component internal property',
  'output-value': 'composite component output value',
  'private-variable': 'composite component private variable',
  'private-data-variable': 'composite component data variable',
  'smart-background': 'smart background',
  'smart-content-color': 'follow smart content color',
  'node': 'output value of another node',
  'input': 'flow function input',
  'schematic-object': 'schematic object',
  'schematic-object-list': 'schematic object list',
  'arbitrary-resource-adapter': 'arbitrary resource adapter',
  'fixed-resource-adapter': 'fixed resource adapter',
  'mapping': 'mapping',
  // TODO: Proper reference names?
  'app-variable-ref': 'app variable',
  'page-variable-ref': 'page variable',
  'parameter-ref': 'page parameter',
  'data-reference': 'data variable',
  'resource': 'data entity',
  'page': 'page ID',
  'component-id': 'component ID',
  'service': 'service',
  // Deprecated, mapped to 'repeated-legacy' instead
  'current': 'property of data item in repeat',
  // Deprecated shit
  'msg': 'legacy logic flow message property',

  /* Backend source types */
  'request': 'request',
  'environment': 'environment variable',
  'external-resource': 'external resource',
};

/**
 * Returns a human readable display name of a source type (`str`, `bool`, `app-variable`, etc.),
 * for displaying in user interface.
 * @param {string} source type of the binding
 * @param {object} schema optional schema, e.g. icon string
 */
export function getSourceDisplayName(source: string, schema?: Schema): string {
  if (source === 'str') {
    const unformattedStringSchema = schema && extractSchema(schema, STRING_UNFORMATTED_SCHEMA);
    if (unformattedStringSchema) {
      // Accepts *unformatted* string
      if (getSchemaAnnotation(unformattedStringSchema, 'uiType') === 'internal-property') {
        return getSourceDisplayName('internal-property', schema);
      }
      if (getSchemaAnnotation(unformattedStringSchema, 'uiType') === 'output-value') {
        return getSourceDisplayName('output-value', schema);
      }
      if (getSchemaAnnotation(unformattedStringSchema, 'uiType') === 'private-variable') {
        return getSourceDisplayName('private-variable', schema);
      }
      if (getSchemaAnnotation(unformattedStringSchema, 'uiType') === 'private-data-variable') {
        return getSourceDisplayName('private-data-variable', schema);
      }
    } else {
      // Does not accept unformatted string, but might require a specific format
      if (isSchemaAssignable({ type: 'string', format: 'icon' }, schema)) {
        return 'static icon';
      }
      const imageSchema = extractSchema({ type: 'string', format: 'web-url' }, schema!);
      if (imageSchema && imageSchema.uiType === 'image-picker') {
        return 'static image';
      }
      if (isSchemaAssignable({ type: 'string', format: 'image' }, schema)) {
        return 'static image';
      }
      if (isSchemaAssignable({ type: 'string', format: 'uri' }, schema)) {
        return 'static URI';
      }
      if (isSchemaAssignable({ type: 'string', format: 'url' }, schema)) {
        return 'static URL';
      }
      if (isSchemaAssignable({ type: 'string', format: 'color' }, schema)) {
        return 'static color';
      }
    }
  }
  if (source === 'object' && schema?.uiType === 'icon') {
    return 'Icon';
  }
  const displayTitle = displayTitlesBySource[source];
  if (displayTitle) {
    return displayTitle;
  }
  if (source.startsWith(REPEAT_NAMESPACE)) {
    return displayTitlesBySource.current;
  }
  // Unknown source type
  return `unknown source ${source}`;
}

const iconsBySource: Record<string, string> = {
  // NOTE: Some string subtypes have other icons!
  'str': 'value-type--string',

  'num': 'value-type--number',
  'object': 'value-type--object',
  'app-variable': 'value-type--app-variable-value',
  'app-variable-ref': 'value-type--app-variable',
  'array': 'value-type--array',
  'bool': 'value-type--boolean',

  'component-id': 'value-type--component-id',
  'component': 'value-type--component-id',
  'self': 'value-type--self',
  'internal-property': 'value-type--internal-property',
  'output-value': 'value-type--output-value',
  'private-variable': 'value-type--private-variable',
  'private-data-variable': 'value-type--private-data-variable',

  'repeated': 'value-type--currently-repeated-property-value',
  'repeated-legacy': 'value-type--currently-repeated-property-value',
  'data': 'value-type--data-instance--2',
  'data-reference': 'value-type--data-instance-value',
  'resource': 'value-type--data-instance',

  'expression': 'value-type--expression',
  'jsonata': 'value-type--expression',

  'page': 'value-type--page-id',
  'page-variable': 'value-type--page-variable-value',
  'page-variable-ref': 'value-type--page-variable',

  'parameter': 'value-type--page-parameter',
  'parameter-ref': 'value-type--page-parameter',

  'service': 'value-type--service',
  'node': 'value-type--service',
  'input': 'value-type--service',
  'computed': 'value-type--service',

  'translation': 'value-type--translated-string',
  'theme': 'theme-variable-link',
  'smart-background': 'theme-variable-link',
  'smart-content-color': 'theme-variable-link',

  'schematic-object': 'value-type--schematic-object',
  'schematic-object-list': 'value-type--schematic-object-list',

  'mapping': 'value-type--mapping',

  // Deprecated, mapped to 'repeated-legacy' instead
  'current': 'value-type--currently-repeated-property-value',
  // Deprecated shit
  'msg': 'exclamation-badge',
  'external-resource': 'value-type--data-instance',
};

/**
 * Returns the SVG icon name for this source type (`str`, `bool`, `app-variable`, etc.).
 * Returns null if icon could not be determined, e.g. when source is null.
 * @param {string} source type of the binding
 * @param {object} schema optional schema
 * @param {string|null} nullIcon default icon when source is null
 */
export function getSourceIcon(source: string, schema: any /* TODO */, nullIcon?: string | null) {
  if (!source) {
    return nullIcon || 'x';
  }
  // If a string option, then use an icon for the format
  if (source === 'str' && schema) {
    const stringSchema = extractSchema(schema, STRING_WILDCARD_SCHEMA);
    if (getSchemaAnnotation(stringSchema!, 'format' as never /* TODO */) === 'icon') {
      return 'value-type--icon';
    }
    if (getSchemaAnnotation(stringSchema!, 'uiType' as never /* TODO */) === 'internal-property') {
      return 'value-type--internal-property';
    }
    if (getSchemaAnnotation(stringSchema!, 'uiType' as never /* TODO */) === 'output-value') {
      return 'value-type--output-value';
    }
    if (getSchemaAnnotation(stringSchema!, 'uiType' as never /* TODO */) === 'private-variable') {
      return 'value-type--private-variable';
    }
    if (getSchemaAnnotation(stringSchema!, 'uiType' as never /* TODO */) === 'private-data-variable') {
      return 'value-type--private-data-variable';
    }
    const webUrlSchema = extractSchema(schema, { type: 'string', format: 'web-url' });
    if (getSchemaAnnotation(webUrlSchema!, 'uiType' as never /* TODO */) === 'image-picker') {
      return 'value-type--image';
    }
  }

  if (source === 'object' && schema?.uiType === 'icon') {
    return 'value-type--icon';
  }

  // Otherwise determine the icon by the source
  return iconsBySource[source] ?? 'source-option--static';
}

const summariesBySource: Record<string, string> = {
  'app-variable': 'Available on all pages in your app',
  'page-variable': 'Available only on the current page',
  'parameter': "Pass data to a page when it's opened",
  'data': 'Access data from a backend',
  'repeated': 'Access repeated data and properties',
  'repeated-legacy': 'Access properties of repeated data',
  'translation': 'Translate text to the current language',
  'theme': 'Access common visual properties of the app',
  'sensor-variable': 'Access device sensor information',
  'system-variable': 'Access device and platform information',
  'smart-content-color': 'Automatic color based on the background',
  'request': 'Access the information of the current request',
  'environment': 'Access the global configuration of the current environment',
};

const descriptionsBySource: Record<string, string> = {
  'str': 'Static text value, inputted in the App Editor. Also called "static string".',
  'num': 'Static number value, inputted in the App Editor. Can be an integer or a decimal value.',
  'bool': 'Static true/false value, inputted in the App Editor. Meant for binary logic, always `true` or `false`.',
  'object':
    "An object whose schema is defined in-place using the binding editor. A custom object's properties can contain both static and dynamic bindings.",
  'array':
    'A list of items whose schema is defined in-place using the binding editor. A custom list can include both static items and ones bound to dynamic values.',
  'app-variable': `App variables are globally available variables that you can use to store and access information between multiple pages.

Their values are reset when the app restarts.

You can define app variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.
`,
  'page-variable': `Page variables are variables that are only available on the page on which they have been defined.

They initialize with any initial value when a page is opened, and cease to exist when the page is closed or otherwise removed from the navigation stack.

You can define page variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.
`,
  'parameter': `Page parameters allow you to pass information to a page when it's opened.

The value of a page parameter is set in the **Open page** flow function that opens the target page, and are read-only once initialized. In web, page parameters are implemented using query parameters.

You can define page parameters in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.
`,
  'data': `Data variables give you access to data from your backend database, integration or client-side data storage.

They function like page variables, but their schema comes from the data entity they're pointing to.

Data variables also include default logic to load data when the page they're on is opened. You can change this logic by selecting a data variable in the **Variables** editor and opening its logic canvas via the **Add logic** button at the bottom of the screen.

Like page variables, data variables cease to exist when the page they're defined on is closed or otherwise removed from the navigation stack.

You can define data variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.
`,
  'repeated-legacy': `**This binding type has been superceded by a newer one!** If you are using 2.x runtime version, please switch the binding type _"Data item in repeat"_ in the previous screen.

Available when the **Repeat with** property is set for the current component or its parent, giving you access to properties of the current item.

For example, if you are repeating a list component with a list of movies, this option gives you access to the current movie's properties. By binding the list item's **Primary label** to the currently repeated item property \`title\`, each list item would render the title of a different movie from the repeat source list.

Currently repeated item properties are also available in the repeated view component's logic canvas, allowing you to e.g. pass the ID of a clicked movie to an **Open page** function.
`,
  'repeated': `Available when the **Repeat with** property is set for the current component or its parent, giving you access the current repeated item, or to one of its properties.

For example, if you are repeating a list component with a list of movies, this option gives you access to the current movie's properties. By binding the list item's **Primary label** to the currently repeated item property \`title\`, each list item would render the title of a different movie from the repeat source list.

Currently repeated item properties are also available in the repeated view component's logic canvas, allowing you to e.g. pass the ID of a clicked movie to an **Open page** function.
`,
  'translation': `Translation variables allow you to easily define text translations in multiple languages.

When the currently active language is changed with the **Set current language** flow function, all translation variable bindings update with the translation for the selected language.

You can define translation variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.
`,
  'theme': `Theme variables describe the colors, typography, borders and other visual properties of your app's view components.

They are most often bound to style properties, such as a **Title** component's **Font** being bound to the \`$fontFamilyHeader\` theme variable.

Theme variables can be configured via the **Theme** section in the global toolbar at the top.
`,
  'sensor-variable': 'Sensor variables are automatically updated values of different sensors available on the device.',
  'system-variable':
    "System variables are automatically updated values of the various aspect of the user's device and operating system.",
  'smart-content-color':
    'Automatically choose the content color based on the current Smart background used in the component hierarchy.',
  'component': `This binding type references a view component property directly, such as the **Value** of an **Input field**.

In most cases, you should rather bind the component property to a dynamic variable, and access the variable instead.`,
  'expression': `Formula functions let you define a dynamic formula to use for the binding value.

Formula functions can perform calculations (\`10/5\`), combine texts (\`"Hello" + "World"\`), access dynamic variables in the app (\`pageVars.title\`), evaluate true/false logic (\`appVars.isRed && appVars.isBlue\`) and most importantly, evaluate functions (\`UPPERCASE("hello world")\`). The functions can also be nested.

For view component property bindings, formula functions are re-evaluated whenever one of their constituents changes. For example, a formula such as \`pageVars.title + ": " + pageVars.subtitle\` will get re-evaluated and thus updated on the app UI whenever either the \`title\` or \`subtitle\` page variable changes.

For flow function input argument bindings, formula functions are evaluated when the flow function gets executed, and re-evaluated on subsequent executions.

Formula function bindings are evaluated and their result is passed to the view component property / flow function input argument regardless of warnings about incompatible schemas, so if you know what you are doing, you can use them to override schema warnings.`,
  'self': 'Property of the currently selected component.',
  'internal-property': `Component internal properties are used to bring in data and values for the component to use, such as the keyboard type of an input, or options list for a dropdown.

A component cannot modify its regular configuration properties from the "inside". An exception is a property set to use two-way binding. In this case, both changes in bound data and user interaction with the component can change the property, such as an input field's **Value**.

Regular configuration properties are read-only in component template editor, while two-way bound properties can be set with the **Set internal property** flow function.`,
  'output-value': `Component output values are a way for the component to "broadcast" data to the rest of the app.

For example, a file upload component could have its "Upload progress" available as an output value.

You cannot bind anything to output values, but they are available via the Binding Editor, so you can use them in formulas or as direct bindings. In component template editor, output values can be set with the **Set output value** flow function.`,
  'private-variable': `Private variables are only usable in component template editor. They can be used to write and read data throughout the component's internal lifecycle, without making that data available from the "outside" when using the component in an app.

Private variables can be set with the **Set private variable** flow function in component template editor.`,
  'input':
    "References an input argument passed to a flow function. Only available inside a flow function's component template editor.",
  'node': `References an output value of another, previous logic node in the current logic chain. Only available for flow function input argument bindings.

For example, **Take photo** would output an object describing the taken photo, which could then be referenced in subsequent logic nodes via this binding type.`,
  'app-variable-ref': `Reference to an app variable. Used only with the **Set app variable** flow function.

You can define app variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.`,
  'page-variable-ref': `Reference to a page variable. Used only with the **Set page variable** flow function.

You can define page variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.`,
  'parameter-ref':
    'You can define page parameters in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.',
  'data-reference': `Reference to a data variable. Used only with the **Set data variable** flow function.

You can define data variables in the **Variables** editor, accessible via the view/variables switch at the top of the view canvas.`,
  'page': 'Reference to a page in the app. Used only with navigation flow functions, such as **Open page**.',
  'resource': `Reference to a data entity configured via the data configurator in the global toolbar. Used with the data flow functions, such as **Create data record**.

You can define data entities in the **Data** editor, accessible via the top bar in the user interface.`,
  'component-id':
    'Reference to a view component on the current page by its ID. Used with flow functions that target a specific view component, such as **Animate component**.',
  'request': 'Information of the request being currently processed',
  'environment': 'Environment variables define the global configuration of the backend deployment',
};

const emptyTitlesBySource: Record<string, string> = {
  'app-variable': 'No app variables defined',
  'app-variable-ref': 'No app variables defined',
  'page-variable': 'No page variables defined',
  'page-variable-ref': 'No page variables defined',
  'parameter': 'No page parameters defined',
  'parameter-ref': 'No page parameters defined',
  'data': 'No data variables defined',
  'data-reference': 'No data variables defined',
  'translation': 'No translation variables defined',
  'resource': 'No data entities defined',
  'repeated': 'No repeated data available',
  'repeated-legacy': 'No repeated data available',
  'request': 'No request available',
  'environment': 'No environment variables defined',
};

export function getSourceDescription(source: string) {
  return descriptionsBySource[source];
}

export function getSourceSummary(source: string) {
  return summariesBySource[source];
}

export function getSourceEmptyTitle(source: string) {
  return emptyTitlesBySource[source];
}

export function getSourceRestrictedAccessDescription(source: string, isInIsolationMode: boolean) {
  if (!isInIsolationMode) {
    return `One-way component properties are not available for binding in the binding editor, expect in composite component template editor. If you want to use these values, you should define a variable and bind it to the property.`;
  }
  return `${upperFirst(
    getSourceDisplayName(source),
  )} and other variable binding types that access the app-level or page-level context are not available in composite component template editor. Instead, you should define an appropriate composite component property, and bind the variable to that property.`;
}

const emptyDescriptionsBySource: Record<string, string> = {
  'page-variable': `Page variables are configured with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

Note that page variables are page-specific, so they are only usable on the page under which they've been defined!

### Steps to add a page variable

1. Switch to the Variable editor via the view/variable switch
2. Select **Page variables** from the left-side menu
3. Click **Add page variable**
4. Give your variable a descriptive name
5. Select the correct type for your variable (e.g. text or number or object)`,
  'page-variable-ref': `Page variables are configured with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

Note that page variables are page-specific, so they are only usable on the page under which they've been defined!

### Steps to add a page variable

1. Switch to the Variable editor via the view/variable switch
2. Select **Page variables** from the left-side menu
3. Click **Add page variable**
4. Give your variable a descriptive name
5. Select the correct type for your variable (e.g. text or number or object)`,
  'app-variable': `App variables are configured with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

App variables are global, so they're available everywhere in your app.

### Steps to add an app variable
1. Switch to the Variable editor via the view/variable switch
2. Select **App variables** from the left-side menu
3. Click **Add app variable**
4. Give your variable a descriptive name
5. Select the correct type for your variable (e.g. text or number or object)`,
  'app-variable-ref': `App variables are configured with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

App variables are global, so they're available everywhere in your app.

### Steps to add an app variable
1. Switch to the Variable editor via the view/variable switch
2. Select **App variables** from the left-side menu
3. Click **Add app variable**
4. Give your variable a descriptive name
5. Select the correct type for your variable (e.g. text or number or object)`,
  'parameter': `Page parameters are configured with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

Note that page parameters are page-specific, so they are only usable on the page under which they've been defined!

### Steps to add a page parameter

1. Switch to the Variable editor via the view/variable switch
2. Select **Page parameter** from the left-side menu
3. Click **Add page parameter**
4. Give your parameter a descriptive name`,
  'parameter-ref': `Page parameters are configured with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

Note that page parameters are page-specific, so they are only usable on the page under which they've been defined!

### Steps to add a page parameter

1. Switch to the Variable editor via the view/variable switch
2. Select **Page parameter** from the left-side menu
3. Click **Add page parameter**
4. Give your parameter a descriptive name`,
  'data': `Data variables are added onto the current page with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

Unlike other variables, their schema is defined by the data entity they utilize.

### Steps to add a data variable

1. Open the **Data configurator** via the global toolbar at the top
2. Add a new resource via the button on the right
3. Define your schema, or for a REST API direct integration, other configuration details
4. After your data entity is correctly configured, close the data configurator
5. Switch to the Variable editor via the view/variable switch
6. Select **Data variables** from the left-side menu
7. Click **Add data variable**
8. Choose the desired resource that the new data variable will utilize
9. Choose the right type for your data variable – collection of records, single record or new record
10. Give your variable a descriptive name`,
  'data-reference': `Data variables are added onto the current page with the **Variable editor**, available via the view/variables switch at the top of the view canvas.

Unlike other variables, their schema is defined by the data entity they utilize.

### Steps to add a data variable

1. Open the **Data configurator** via the global toolbar at the top
2. Add a new resource via the button on the right
3. Define your schema, or for a REST API direct integration, other configuration details
4. After your data entity is correctly configured, close the data configurator
5. Switch to the Variable editor via the view/variable switch
6. Select **Data variables** from the left-side menu
7. Click **Add data variable**
8. Choose the desired resource that the new data variable will utilize
9. Choose the right type for your data variable – collection of records, single record or new record
10. Give your variable a descriptive name`,
  'node': `This binding type is only available for flow function nodes that are connected to a previous node in the logic flow.

To make this binding type available, simply connect the output of another node to the input of this node.

Note that some flow functions do not output anything.`,
  'internal-property': `This binding type is only available in component template editor for composite view components.

To define a new component property, click **Properties** in the component template editor toolbar.`,
  'output-value': `This binding type is only available in component template editor for composite view components.

To define a new output value, click **Properties** in the component template editor toolbar.`,
  'private-variable': `This binding type is only available in component template editor for composite view components.

To define a new private variable, click **Properties** in the component template editor toolbar.`,
  'input': `This binding type is only available in component template editor for custom flow functions.

To define a new flow function input, click **Edit properties** in the component template editor toolbar.`,
};

const booleansByStringRepresentations: Record<string, boolean> = {
  true: true,
  false: false,
};

export function getSourceEmptyDescription(source: string) {
  return emptyDescriptionsBySource[source];
}

/**
 * Returns a binding object that once resolved will result
 * in the given constant value. It can be a primitive or even
 * an object or an array, but it cannot be null.
 * Returns null if the value is null.
 * @param {*} value any value
 */
export function getStaticBindingOfValue(value: any): { source: string; key: unknown } | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    return { source: 'str', key: value };
  }
  if (typeof value === 'number') {
    return { source: 'num', key: value };
  }
  if (typeof value === 'boolean') {
    return { source: 'bool', key: value };
  }
  if (Array.isArray(value)) {
    return {
      source: 'array',
      key: value.map((item) => getStaticBindingOfValue(item)).filter((item) => item != null),
    };
  }
  if (value != null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.keys(value).forEach((property) => {
      const binding = getStaticBindingOfValue(value[property]);
      if (binding != null) {
        result[property] = binding;
      }
    });
    return {
      source: 'object',
      key: result,
    };
  }
  return null;
}

/**
 * Migrates any string, number or boolean bindings to corresponding `str`, `num` or `bool`
 * binding objects. Also migrates a binding object where instead of sources 'array'
 * and 'object', the sources are themselves arrays and objects. This legacy format
 * is still used in node-red logic nodes.
 * @param {object | boolean | string | number | null | undefined} binding Binding object
 */
export function migrateBinding(binding: any /* TODO */): any {
  // If the value is a string, boolean or number instead of an object,
  // then for components this mean a static binding. Convert to that
  // format for internal editing.
  if (typeof binding === 'string') {
    return { source: 'str', key: binding };
  }
  if (typeof binding === 'boolean') {
    return { source: 'bool', key: binding };
  }
  if (typeof binding === 'number') {
    return { source: 'num', key: binding };
  }
  if (!binding) {
    return binding; // null or undefined
  }
  const { source, key, value } = binding;
  // Migrate a style binding using `value` to the general format using `key`
  if (typeof key === 'undefined' && typeof value !== 'undefined') {
    const newBinding = { ...binding, key: value };
    delete newBinding.value;
    return migrateBinding(newBinding);
  }
  // If key is an array of sources, then convert to a format where
  // source is `array` and key is an array of bindings.
  if (Array.isArray(source)) {
    const bindings = source.map((itemSource, index) => {
      const itemKey = key[index];
      return migrateBinding({ source: itemSource, key: itemKey });
    });
    return { ...binding, source: 'array', key: bindings };
  }
  // If key is an object of sources, then convert to a format where
  // source is `object` and key is an object of bindings as values.
  if (source && typeof source === 'object') {
    const bindings: Record<string, any /* TODO */> = {};
    Object.keys(source).forEach((propName) => {
      const propSource = source[propName];
      const propKey = key[propName];
      bindings[propName] = migrateBinding({ source: propSource, key: propKey });
    });
    return { ...binding, source: 'object', key: bindings };
  }
  if (typeof source !== 'string') {
    return binding;
  }
  // For object sources, recursively migrate nested bindings
  if (source === 'object') {
    return {
      ...binding,
      key: mapValues(key, migrateBinding),
    };
  }
  // For array sources, recursively migrate nested bindings
  if (source === 'array') {
    return {
      ...binding,
      key: Array.isArray(key) ? key.map(migrateBinding) : [],
    };
  }
  // If the source is 'smart-background' then migrate nested binding
  if (source === 'smart-background' && key?.backgroundOverride != null) {
    return {
      ...binding,
      key: {
        ...key,
        backgroundOverride: migrateBinding(key.backgroundOverride),
      },
    };
  }
  // If the source is 'components', then edit as 'component' and
  // remove the 'components.' prefix from the key.
  if (source === 'components') {
    return {
      ...binding,
      source: 'component',
      key: typeof key === 'string' ? key.replace(/^components?\./, '') : key,
    };
  }
  // If the source is 'current' then convert to the preferred 'repeated' source type.
  if (source === defaultRepeatName) {
    return {
      ...binding,
      source: 'repeated-legacy',
      key: source + (key ? `.${key}` : ''),
    };
  }
  // If the source starts with 'repeated_' then convert to the preferred 'repeated' source type.
  if (source.startsWith('repeated_')) {
    return {
      ...binding,
      source: 'repeated-legacy',
      // NOTE: Remove the 'repeated_' prefix!
      key: source.slice('repeated_'.length) + (key ? `.${key}` : ''),
    };
  }
  return binding;
}

/**
 * Converts the given well-formatted binding object to the format used in style properties.
 * In other words:
 * - all 'str', 'bool' and 'num' source types are converted to constant values instead of objects
 * - binding objects use `value` property instead of `key`
 * @param {*} binding binding in common format
 */
export function convertBindingToStylePropertyFormat(binding: any /* TODO */): StyleBinding {
  if (binding == null || typeof binding !== 'object' || Array.isArray(binding)) {
    return binding;
  }
  // If bound to a static string, boolean, or number then
  // convert them to a simple value instead of an object.
  // This converstion could be removed when the runtime supports
  // these binding sources for component bindings.
  const { source, value, key = value, ...meta } = binding;
  if (source === 'str' && typeof key === 'string') {
    return key;
  }
  if (source === 'num') {
    const number = typeof key === 'number' ? key : parseFloat(key);
    if (typeof number === 'number' && !Number.isNaN(number)) {
      return number;
    }
  }
  if (source === 'bool') {
    const bool = typeof key === 'boolean' ? key : booleansByStringRepresentations[key];
    if (typeof bool === 'boolean') {
      return bool;
    }
  }
  if (source === 'smart-background') {
    return {
      source,
      ...meta,
      value: key.colorOverride
        ? {
            ...key,
            colorOverride: convertBindingToStylePropertyFormat(key.colorOverride),
          }
        : key,
    };
  }
  if (typeof value !== 'undefined') {
    return binding;
  }
  return { source, ...meta, value: key };
}

/**
 * Converts a binding that uses a 'repeated' source type to the more
 * compatible 'current' or 'repeated_xxxx' version.
 */
export function convertBindingToLegacyRepeatFormat(binding: any /* TODO */): any {
  if (binding && binding.source === 'repeated-legacy' && typeof binding.key === 'string' && binding.key) {
    const [source, ...keyPath] = binding.key.split('.');
    if (keyPath.length) {
      const repeatSource = source === defaultRepeatName ? source : `repeated_${source}`;
      return { ...binding, source: repeatSource, key: keyPath.join('.') };
    }
  }
  return binding;
}

/**
 * Converts the given well-formatted binding object to legacy node logic binding format.
 * You can convert it back to the good format with `migrateBinding`.
 * @param {object} binding binding in common format
 */
export function convertBindingToLegacyLogicFormat(binding: any /* TODO */): NodeRedBinding {
  if (!binding) {
    return binding;
  }
  if (binding.source === 'array') {
    // Convert back to a format where both `source` and `key` are arrays
    const subBindings: any[] /* TODO */ = binding.key.map((subBinding: any /* TODO */) =>
      convertBindingToLegacyLogicFormat(subBinding),
    );
    return {
      ...binding,
      source: subBindings.map(({ source }) => source),
      key: subBindings.map(({ key }) => key),
    };
  }
  if (binding.source === 'object') {
    // Convert back to a format where both `source` and `key` are objects
    const subBindings = mapValues(
      pickBy(binding.key, (subBinding) => subBinding != null),
      (subBinding) => convertBindingToLegacyLogicFormat(subBinding),
    );
    return {
      ...binding,
      source: mapValues(subBindings, (subBinding) =>
        typeof subBinding === 'object' && subBinding != null ? subBinding.source : undefined,
      ),
      key: mapValues(subBindings, (subBinding) =>
        typeof subBinding === 'object' && subBinding != null ? subBinding.key : undefined,
      ),
    };
  }
  if (binding.source === 'component') {
    // Convert source back to 'components' and add the 'components.' prefix to the key
    const { key } = binding;
    return {
      ...binding,
      source: 'components',
      key: typeof key === 'string' && !/^components?\./.test(key) ? `components.${key}` : key,
    };
  }
  return convertBindingToLegacyRepeatFormat(binding);
}

/**
 * Converts the given well-formatted binding object to legacy component UI property binding.
 * In other words, all 'str', 'bool' and 'num' source types are converted to constant values
 * instead of objects.
 * @param {object} binding binding in common format
 */
export function convertBindingToUiComponentPropertyFormat(binding: any /* TODO */): any {
  if (!binding) {
    return binding;
  }
  // If bound to a static string, boolean, or number then
  // convert them to a simple value instead of an object.
  // This converstion could be removed when the runtime supports
  // these binding sources for component bindings.
  const { source, key } = binding;
  if (source === 'str' && typeof key === 'string') {
    return key;
  }
  if (source === 'num') {
    const value = typeof key === 'number' ? key : parseFloat(key);
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
  }
  if (source === 'bool') {
    const value = typeof key === 'boolean' ? key : booleansByStringRepresentations[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return convertBindingToLegacyRepeatFormat(binding);
}

/**
 * Generates a new binding object with source `array` using the given schema
 * and known reference options.
 *
 * The binding direction is required to determine what reference options are compatible.
 * It can be either null (legacy), "inbound", "outbound", "two-way" or "private".
 *
 * @param {object} schema required schema
 * @param {array} referenceOptions array of available references
 * @param {string} bindingType direction of the target binding
 */
export function getBlankArrayBinding(
  schema: any /* TODO */,
  referenceOptions?: any[] /* TODO */,
  bindingType?: ComponentPropertyBindingType,
): any {
  // If the schema has a default value then generate a binding using that
  if (schema && Array.isArray(schema.default)) {
    if (isSchemaAssignable(getConstantSchema(schema.default), schema)) {
      return getStaticBindingOfValue(schema.default);
    }
  }
  // Generate an array binding with minimum number of required bindings
  const minItems = schema?.minItems ?? 0;
  const key = [];
  while (key.length < minItems) {
    const itemSchema = getArrayItemSchema(schema);
    const itemBinding = getBlankBindingForSchema(itemSchema, referenceOptions, bindingType);
    // NOTE: Could be null binding, but we still want to have the minimun number of bindings
    key.push(itemBinding || DUMMY_BINDING);
  }
  return { source: 'array', key };
}

/**
 * Generates a new binding object with source `object` using the given schema
 * and known reference options.
 *
 * The binding direction is required to determine what reference options are compatible.
 * It can be either null (legacy), "inbound", "outbound", "two-way" or "private".
 *
 * @param {object} schema required schema
 * @param {array} referenceOptions array of available references
 * @param {string} bindingType direction of the target binding
 */
export function getBlankObjectBinding(
  schema: any /* TODO */,
  referenceOptions?: any[] /* TODO */,
  bindingType?: ComponentPropertyBindingType,
): any {
  // If the schema has a default value then generate a binding using that
  if (schema && schema.default != null && typeof schema.default === 'object' && !Array.isArray(schema.default)) {
    return getStaticBindingOfValue(schema.default);
  }
  if (schema?.anyOf?.length) {
    return getBlankObjectBinding(schema.anyOf[0]);
  }
  // Create an object with blank bindings for each property
  const result: Record<string, unknown> = {};
  const requiredPropNames = getRequiredObjectSchemaProperties(schema);
  if (requiredPropNames) {
    for (let index = 0; index < requiredPropNames.length; index += 1) {
      const propertyName = requiredPropNames[index];
      const propSchema = getObjectPropertySchema(schema, propertyName);
      const binding = getBlankBindingForSchema(propSchema, referenceOptions, bindingType);
      if (!binding) {
        return null;
      }
      result[propertyName] = binding;
    }
  }
  return { source: 'object', key: result };
}

/**
 * Generates a new binding object that matches the given schema,
 * using the given known reference options. If no valid binding can be
 * generated then returns `null`.
 *
 * The binding direction is required to determine what reference options are compatible.
 * It can be either null (legacy), "inbound", "outbound", "two-way" or "private".
 *
 * @param {object} schema required schema
 * @param {array} referenceOptions array of available references
 * @param {string} [bindingType] direction of the target binding
 */
export function getBlankBindingForSchema(
  schema: any /* TODO */,
  referenceOptions: ReferenceOption[] = [],
  bindingType?: ComponentPropertyBindingType,
): any {
  // If schema has a default value, then generate a binding from it,
  // unless the binding direction requires a writable binding.
  const requiresWritable = bindingType === 'outbound' || bindingType === 'two-way';
  if (!requiresWritable) {
    if (schema && schema.default != null) {
      return getStaticBindingOfValue(schema.default);
    }
  }
  // For union types, use the first possible sub-type that generates a valid binding
  if (schema && schema.anyOf) {
    for (let i = 0; i < schema.anyOf.length; i += 1) {
      const subSchema = schema.anyOf[i];
      const binding = getBlankBindingForSchema(subSchema, referenceOptions, bindingType);
      if (binding != null) {
        return binding;
      }
    }
    return null;
  }
  // Attempt using static binding types, unless a writable binding is required
  if (!requiresWritable) {
    const stringSchema = extractSchema(schema, STRING_WILDCARD_SCHEMA);
    if (stringSchema) {
      const defaultValue = getDefaultValueForSchema(stringSchema);
      return { source: 'str', key: typeof defaultValue === 'string' ? defaultValue : '' };
    }
    const numberSchema = extractSchema(schema, NUMBER_SCHEMA);
    if (numberSchema) {
      const defaultValue = getDefaultValueForSchema(numberSchema);
      return { source: 'num', key: typeof defaultValue === 'number' ? defaultValue : 0 };
    }
    const booleanSchema = extractSchema(schema, BOOLEAN_SCHEMA);
    if (booleanSchema) {
      const defaultValue = getDefaultValueForSchema(booleanSchema);
      return { source: 'bool', key: typeof defaultValue === 'boolean' ? defaultValue : false };
    }
    const arraySchema = extractSchema(schema, ARRAY_SCHEMA);
    if (arraySchema) {
      return getBlankArrayBinding(arraySchema, referenceOptions);
    }
    const objectSchema = extractSchema(schema, OBJECT_SCHEMA);
    if (objectSchema) {
      return getBlankObjectBinding(objectSchema, referenceOptions);
    }
    const btpDestinationSchema = extractSchema(schema, BTP_DESTINATION_REFERENCE_SCHEMA);
    if (btpDestinationSchema) {
      return { source: 'str', key: '' };
    }
    const httpRequestBodySchema = extractSchema(schema, HTTP_REQUEST_BODY_REFERENCE_SCHEMA);
    if (httpRequestBodySchema) {
      return { source: 'str', key: 'json' };
    }
    const httpRequestMethodSchema = extractSchema(schema, HTTP_REQUEST_METHOD_REFERENCE_SCHEMA);
    if (httpRequestMethodSchema) {
      return { source: 'str', key: 'GET' };
    }
  }
  // Try to find a binding from the reference options
  // Otherwise find a reference from the reference options
  const bindableReferenceOptions = referenceOptions.filter(
    (ref) => !ref.hidden && isReferenceBindableToDirection(ref, bindingType),
  );
  // Primarily try to find something that match with strict type checking,
  // but if not found, then try to find with tolerant type checking.
  const reference =
    bindableReferenceOptions.find((ref) => isBindingSchemaCompatible(ref.schema, schema, bindingType, true)) ||
    bindableReferenceOptions.find((ref) => isBindingSchemaCompatible(ref.schema, schema, bindingType, false));
  if (reference) {
    return { source: reference.source, key: reference.key };
  }
  // Could not generate a valid binding
  return null;
}

/**
 * Changes the source type of the giving binding, ensuring that the `key` also
 * has a meaningful value after the change. It attempts to automatically convert
 * the `key` and `preview` for the new source type, if possible. Otherwise, the `key`
 * is reset to a valid default value.
 *
 * @param {object} binding current binding object
 * @param {string} source new source type
 * @param {object} schema optional schema
 * @param {array} referenceOptions array of available references
 */
export function setBindingSource(
  binding: any /* TODO */,
  source: string,
  schema: any /* TODO */,
  referenceOptions: any[] = [],
) {
  const { preview: _preview, source: oldSource, ...bindingBase } = binding || {};
  if (oldSource === source) {
    // No changes in source
    return binding;
  }
  if (source === 'str') {
    const valueSchema = extractSchema(schema, STRING_WILDCARD_SCHEMA);
    const defaultValue = getDefaultValueForSchema(valueSchema);
    return { ...bindingBase, source, key: typeof defaultValue === 'string' ? defaultValue : '' };
  }
  if (source === 'num') {
    const valueSchema = extractSchema(schema, NUMBER_SCHEMA);
    const defaultValue = getDefaultValueForSchema(valueSchema);
    return { ...bindingBase, source, key: typeof defaultValue === 'number' ? defaultValue : 0 };
  }
  if (source === 'bool') {
    const valueSchema = extractSchema(schema, BOOLEAN_SCHEMA);
    const defaultValue = getDefaultValueForSchema(valueSchema);
    return { ...bindingBase, source, key: typeof defaultValue === 'boolean' ? defaultValue : false };
  }
  if (source === 'array') {
    return { ...bindingBase, ...getBlankArrayBinding(schema, referenceOptions) };
  }
  if (source === 'object') {
    return { ...bindingBase, ...getBlankObjectBinding(schema, referenceOptions) };
  }
  if (source === 'expression') {
    return { ...bindingBase, source, key: convertBindingToExpression(binding) };
  }
  // Otherwise find a reference from the reference options
  const reference = referenceOptions.find(
    (ref) => !ref.hidden && ref.source === source && isSchemaAssignable(ref.schema || ANY_SCHEMA, schema),
  );
  if (reference) {
    return { ...bindingBase, source, key: reference.key };
  }
  // Unknown source!
  return { ...bindingBase, source, key: '' };
}

/**
 * Returns a schema for the value that the given binding would result to.
 * @param {object} binding binding object with `source` and `key`
 * @param {array} referenceOptions list of available reference options
 * @param {schema} schemaHint default schema
 */
export function getSchemaOfBinding(
  binding: any /* TODO */,
  referenceOptions: any[] /* TODO */,
  schemaHint?: any,
  expressionContextSchema?: any,
): Schema {
  if (binding == null) {
    return schemaHint || null;
  }
  const { source, value, key = value } = binding;
  // Recursive bindings
  if (source === 'array') {
    const itemSchemaHint = schemaHint && getArrayItemSchema(schemaHint);
    if (!Array.isArray(key)) {
      return { type: 'array', items: UNKNOWN_SCHEMA };
    }
    if (!key.length) {
      return { type: 'array', items: UNKNOWN_SCHEMA, maxLength: 0, enum: [[]] };
    }
    const items = getUnionSchema(
      key.map((itemBinding) =>
        getSchemaOfBinding(itemBinding, referenceOptions, itemSchemaHint, expressionContextSchema),
      ),
    );
    return { type: 'array', items };
  }
  if (source === 'object') {
    const properties: Record<string, Schema> = {};
    if (key != null && typeof key === 'object' && !Array.isArray(key)) {
      Object.keys(key).forEach((propertyName) => {
        const valueBinding = key[propertyName];
        const valueSchemaHint = getObjectPropertySchema(schemaHint, propertyName);
        properties[propertyName] = getSchemaOfBinding(
          valueBinding,
          referenceOptions,
          valueSchemaHint,
          expressionContextSchema,
        );
      });
    }
    return { type: 'object', properties };
  }
  // Handle non-recursive bindings
  if (source === 'expression') {
    const expression = importCachedExpression(key);
    if (expression.syntaxError) {
      return {};
    }
    const contextSchema = expressionContextSchema || getExpressionContextSchema(referenceOptions, true);
    const exprSchema = expression.analyze(contextSchema).schema;
    // Use schema hint as the result if the expression schema is incompatible
    return isSchemaAssignable(exprSchema, schemaHint) ? exprSchema : schemaHint;
  }
  // Handle schema hint
  if (schemaHint) {
    // Call recursively without the `schemaHint` parameter!
    let schema = getSchemaOfBinding(binding, referenceOptions, null, expressionContextSchema);
    if (!schema) {
      return schemaHint;
    }
    if (!schemaHint.enum) {
      // Only include the enum to the result schema if the hint also has an enum
      schema = omit(schema, 'enum');
    }
    // If the schema allows many types, use the hint to reduce the number of types
    if (
      !isSubsetSchema(schemaHint, schema) &&
      !isSubsetSchema(schemaHint, CLOUD_FUNCTION_REFERENCE_SCHEMA as any /* TODO */, true) &&
      !isSubsetSchema(schemaHint, SPA_PROCESS_DEFINITION_REFERENCE_SCHEMA as any, true) &&
      !isSubsetSchema(schemaHint, BUILD_ACTION_REFERENCE_SCHEMA as any, true) &&
      !isSubsetSchema(schemaHint, BTP_DESTINATION_REFERENCE_SCHEMA as any, true) &&
      !isSubsetSchema(schemaHint, HTTP_REQUEST_BODY_REFERENCE_SCHEMA as any, true) &&
      !isSubsetSchema(schemaHint, HTTP_REQUEST_METHOD_REFERENCE_SCHEMA as any, true)
    ) {
      return schema;
    }
    const mergedSchema = { ...schemaHint };
    if (schema.examples) {
      mergedSchema.examples = schema.examples;
    }
    if (schema.enum) {
      mergedSchema.enum = schema.enum;
    }
    if (schema.default) {
      mergedSchema.default = schema.default;
    }
    return mergedSchema;
  }
  if (typeof binding !== 'object') {
    return getConstantSchema(binding);
  }
  if (source === 'str' && typeof key === 'string') {
    return { ...getConstantSchema(key), format: '*' };
  }
  if (source === 'num') {
    return getConstantSchema(parseFloat(key));
  }
  if (source === 'bool') {
    return getConstantSchema(key === 'true' || key === true);
  }
  if (source === 'computed' || source === 'msg' || source === 'jsonata') {
    return {}; // Unknown schema
  }
  const reference = referenceOptions.find((ref) => ref.source === source && isEqual(ref.key, key));
  return reference ? reference.schema || {} : (null as unknown as Schema);
}

/**
 * Returns a schema for editing a preview value for a binding, when the
 * target type is the given schema object, and the current binding source type is
 * the given string. Returns null if providing a preview value is not supported
 * by the target schema or the source type.
 * @param {object} schema target schema
 * @param {string} source target binding source type
 */
export function getBindingPreviewSchema(schema: any /* TODO */, source: string) {
  if (!source || ['str', 'num', 'bool', 'object', 'array'].indexOf(source) >= 0) {
    return null;
  }
  return (
    extractSchema(schema, STRING_WILDCARD_SCHEMA) ||
    extractSchema(schema, NUMBER_SCHEMA) ||
    extractSchema(schema, BOOLEAN_SCHEMA)
  );
}

/**
 * Whether or not the given preview value should be used for preview purposes.
 * Null, undefined and empty strings are not considered valid preview values.
 * This helper exists to ensure consistent logic when checking for preview values.
 */
export function isEffectivePreviewValue<T>(value: T): value is Exclude<T, null | undefined | ''> {
  return value != null && value !== '';
}

/**
 * Recursively maps the binding and all of its nested bindings to a new binding
 * object. Useful to e.g. fix or migrate references in bindings to new values.
 * If the mapper function gets the old binding as a parameter, and it must return
 * either a new binding or the old one. If it returns nullish value, then the binding
 * is "removed", e.g. from nested object or array bindings.
 *
 * @param {object} binding a (migrated) binding object
 * @param {function} mapper function mapping the binding to a new one
 */
export function transformBinding(binding: any /* TODO */, mapper: (val: any) => any) {
  const newBinding = mapper(migrateBinding(binding));
  if (!newBinding) {
    return newBinding;
  }
  const migratedBinding = migrateBinding(newBinding);
  const { source, key } = migratedBinding;
  if (source === 'object') {
    const newKey = transformBindings(key, mapper);
    return { ...migratedBinding, key: newKey };
  }
  if (source === 'array') {
    const newKey: any[] = [];
    key.forEach((oldItemBinding: any) => {
      const newItemBinding = transformBinding(oldItemBinding, mapper);
      if (newItemBinding != null) {
        newKey.push(newItemBinding);
      }
    });
    return { ...migratedBinding, key: newKey };
  }
  return newBinding;
}

/**
 * Takes an object where values are bindings, and performs `transformBinding` for each of them,
 * returning a new object where values are transformed binding objects.
 *
 * @param {object} bindings a mapping of (migrated) binding objects
 * @param {function} mapper function mapping the binding to a new one
 */
export function transformBindings(bindings: Record<string, unknown> /* TODO */, mapper: (val: any) => any) {
  const newBindings: Record<string, any> /* TODO */ = {};
  Object.keys(bindings).forEach((propName) => {
    const newPropertyBinding = transformBinding(bindings[propName], mapper);
    if (newPropertyBinding != null) {
      newBindings[propName] = newPropertyBinding;
    }
  });
  return newBindings;
}

/**
 * Returns a human-readable, lower-case display text for a binding type,
 * which is either "inbound", "outbound", "private" (defaulting to "inbound")
 * @param {string} bindingType binding type
 * @param {boolean} inIsolation whether we are inside of the component
 */
export function getBindingTypeText(bindingType: ComponentPropertyBindingType, inIsolation?: boolean) {
  switch (bindingType) {
    case 'outbound':
      return 'Output value';
    case 'private':
      return 'Private variable';
    default:
      return inIsolation ? 'Internal property' : 'Property';
  }
}
